import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';

export const formatTime = (timeInSeconds, showAllParts = false) => {
  if (!timeInSeconds) {
    return '';
  }

  const totalSeconds = Math.floor(timeInSeconds);

  // Legacy behavior - show only the largest unit
  if (!showAllParts) {
    if (totalSeconds < 60) {
      return `${totalSeconds}s`;
    }

    if (totalSeconds < 3600) {
      const minutes = Math.floor(totalSeconds / 60);
      return `${minutes}m`;
    }

    if (totalSeconds < 86400) {
      const hours = Math.floor(totalSeconds / 3600);
      return `${hours}h`;
    }

    const days = Math.floor(totalSeconds / 86400);
    return `${days}d`;
  }

  // New behavior - show all relevant parts
  const parts = [];

  // Calculate days
  if (totalSeconds >= 86400) {
    const days = Math.floor(totalSeconds / 86400);
    parts.push(`${days}d`);
  }

  // Calculate hours
  const remainingAfterDays = totalSeconds % 86400;
  if (remainingAfterDays >= 3600) {
    const hours = Math.floor(remainingAfterDays / 3600);
    parts.push(`${hours}h`);
  }

  // Calculate minutes
  const remainingAfterHours = remainingAfterDays % 3600;
  if (remainingAfterHours >= 60) {
    const minutes = Math.floor(remainingAfterHours / 60);
    parts.push(`${minutes}m`);
  }

  // Calculate seconds
  const seconds = remainingAfterHours % 60;
  if (seconds > 0 || parts.length === 0) {
    parts.push(`${seconds}s`);
  }

  return parts.join('\u00A0');
};

export const GROUP_BY_FILTER = {
  1: { id: 1, period: 'day' },
  2: { id: 2, period: 'week' },
  3: { id: 3, period: 'month' },
  4: { id: 4, period: 'year' },
};

export const GROUP_BY_OPTIONS = {
  DAY: {
    id: 'DAY',
    period: 'day',
    translationKey: 'REPORT.GROUPING_OPTIONS.DAY',
  },
  WEEK: {
    id: 'WEEK',
    period: 'week',
    translationKey: 'REPORT.GROUPING_OPTIONS.WEEK',
  },
  MONTH: {
    id: 'MONTH',
    period: 'month',
    translationKey: 'REPORT.GROUPING_OPTIONS.MONTH',
  },
  YEAR: {
    id: 'YEAR',
    period: 'year',
    translationKey: 'REPORT.GROUPING_OPTIONS.YEAR',
  },
};

export const DATE_RANGE_OPTIONS = {
  TODAY: {
    id: 'TODAY',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.TODAY',
    offset: 0,
    groupByOptions: [],
  },
  YESTERDAY: {
    id: 'YESTERDAY',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.YESTERDAY',
    offset: 1,
    groupByOptions: [],
  },
  LAST_7_DAYS: {
    id: 'LAST_7_DAYS',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS',
    offset: 6,
    groupByOptions: [GROUP_BY_OPTIONS.DAY],
  },
  LAST_30_DAYS: {
    id: 'LAST_30_DAYS',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_30_DAYS',
    offset: 29,
    groupByOptions: [GROUP_BY_OPTIONS.DAY, GROUP_BY_OPTIONS.WEEK],
  },
  THIS_MONTH: {
    id: 'THIS_MONTH',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.THIS_MONTH',
    offset: null,
    groupByOptions: [GROUP_BY_OPTIONS.DAY, GROUP_BY_OPTIONS.WEEK],
  },
  LAST_MONTH: {
    id: 'LAST_MONTH',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_MONTH',
    offset: null,
    groupByOptions: [GROUP_BY_OPTIONS.DAY, GROUP_BY_OPTIONS.WEEK],
  },
  LAST_3_MONTHS: {
    id: 'LAST_3_MONTHS',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_3_MONTHS',
    offset: 89,
    groupByOptions: [
      GROUP_BY_OPTIONS.DAY,
      GROUP_BY_OPTIONS.WEEK,
      GROUP_BY_OPTIONS.MONTH,
    ],
  },
  LAST_6_MONTHS: {
    id: 'LAST_6_MONTHS',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_6_MONTHS',
    offset: 179,
    groupByOptions: [GROUP_BY_OPTIONS.WEEK, GROUP_BY_OPTIONS.MONTH],
  },
  LAST_YEAR: {
    id: 'LAST_YEAR',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.LAST_YEAR',
    offset: 364,
    groupByOptions: [GROUP_BY_OPTIONS.WEEK, GROUP_BY_OPTIONS.MONTH],
  },
  CUSTOM_DATE_RANGE: {
    id: 'CUSTOM_DATE_RANGE',
    translationKey: 'REPORT.DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE',
    offset: null,
    groupByOptions: [
      GROUP_BY_OPTIONS.DAY,
      GROUP_BY_OPTIONS.WEEK,
      GROUP_BY_OPTIONS.MONTH,
      GROUP_BY_OPTIONS.YEAR,
    ],
  },
};

/**
 * Resolves a reports date-range preset into `{ from, to }` UNIX timestamps.
 * Month presets are anchored to calendar-month boundaries; day presets subtract
 * `offset` days from today. `customDateRange` is a `[start, end]` Date tuple,
 * only used for the CUSTOM_DATE_RANGE preset.
 */
export const getReportDateRange = (dateRange, customDateRange = []) => {
  const { id, offset } = dateRange;
  const now = new Date();

  switch (id) {
    case DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id:
      return {
        from: getUnixStartOfDay(customDateRange[0]),
        to: getUnixEndOfDay(customDateRange[1]),
      };
    case DATE_RANGE_OPTIONS.THIS_MONTH.id:
      return {
        from: getUnixStartOfDay(startOfMonth(now)),
        to: getUnixEndOfDay(now),
      };
    case DATE_RANGE_OPTIONS.LAST_MONTH.id: {
      const lastMonth = subMonths(now, 1);
      return {
        from: getUnixStartOfDay(startOfMonth(lastMonth)),
        to: getUnixEndOfDay(endOfMonth(lastMonth)),
      };
    }
    case DATE_RANGE_OPTIONS.YESTERDAY.id: {
      const yesterday = subDays(now, 1);
      return {
        from: getUnixStartOfDay(yesterday),
        to: getUnixEndOfDay(yesterday),
      };
    }
    default:
      return {
        from: getUnixStartOfDay(subDays(now, offset)),
        to: getUnixEndOfDay(now),
      };
  }
};

export const CHART_FONT_FAMILY =
  'Inter,-apple-system,system-ui,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif';

export const DEFAULT_LINE_CHART = {
  type: 'line',
  fill: false,
  borderColor: '#779BBB',
  pointBackgroundColor: '#779BBB',
};

export const DEFAULT_BAR_CHART = {
  type: 'bar',
  backgroundColor: 'rgb(31, 147, 255)',
};

const createChartConfig = yAxisTickCallback => ({
  datasets: [DEFAULT_BAR_CHART],
  scales: {
    x: {
      ticks: {
        fontFamily: CHART_FONT_FAMILY,
      },
      grid: {
        drawOnChartArea: false,
      },
    },
    y: {
      type: 'linear',
      position: 'left',
      ticks: {
        fontFamily: CHART_FONT_FAMILY,
        beginAtZero: true,
        stepSize: 1,
        callback: yAxisTickCallback,
      },
      grid: {
        drawOnChartArea: false,
      },
    },
  },
});

export const DEFAULT_CHART = createChartConfig((value, index, ticks) => {
  if (!index || index === ticks.length - 1) {
    return value;
  }
  return '';
});

export const TIME_CHART_CONFIG = createChartConfig((value, index, values) => {
  if (!index || index === values.length - 1) {
    return formatTime(value);
  }
  return '';
});

export const METRIC_CHART = {
  conversations_count: DEFAULT_CHART,
  incoming_messages_count: DEFAULT_CHART,
  outgoing_messages_count: DEFAULT_CHART,
  avg_first_response_time: TIME_CHART_CONFIG,
  reply_time: TIME_CHART_CONFIG,
  avg_resolution_time: TIME_CHART_CONFIG,
  resolutions_count: DEFAULT_CHART,
  bot_resolutions_count: DEFAULT_CHART,
  bot_handoffs_count: DEFAULT_CHART,
};

export const OVERVIEW_METRICS = {
  open: 'OPEN',
  unattended: 'UNATTENDED',
  unassigned: 'UNASSIGNED',
  pending: 'PENDING',
  online: 'ONLINE',
  busy: 'BUSY',
  offline: 'OFFLINE',
};
